import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { elegantMenuProps } from '../../utils/menuStyles';
import {
  Box, Typography, Button, TextField, IconButton, Menu, MenuItem,
  Chip, InputAdornment, Paper, Stack, Divider, Skeleton,
  useTheme, alpha,
} from '@mui/material';
import {
  Add, MoreVert, Edit, Delete, Search, Sell,
  StarBorder,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import feeCategoryService from '../../services/feeCategoryService';
import { FeeCategory } from '../../types/fees';
import ConfirmationDialog from '../common/ConfirmationDialog';
import FeeCategoryDialog from './FeeCategoryDialog';
import { useDebounce } from '../../hooks/useDebounce';

/**
 * Fee Categories settings — the school's fee-head chart. Single-level
 * (no sub-categories; a plan line item is category + amount, no deeper
 * taxonomy). Writes gated by `settings_fee_categories`.
 *
 * Visual tone: subtle indigo/blue accent so it reads as adjacent to the
 * money-in family (income categories use success/green) without
 * colliding with it.
 */
const FeeCategoriesSettings: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const hasWrite = canWrite('settings_fee_categories');

  const accent = theme.palette.info.main;

  const [categories, setCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FeeCategory | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeeCategory | null>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<FeeCategory | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cats = await feeCategoryService.getFeeCategories({
        search: debouncedSearch || undefined,
      });
      setCategories(cats);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load fee categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (cat: FeeCategory) => {
    setEditing(cat);
    setDialogOpen(true);
    closeMenu();
  };
  const openDelete = (cat: FeeCategory) => {
    setDeleteTarget(cat);
    setDeleteOpen(true);
    closeMenu();
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, cat: FeeCategory) => {
    setMenuAnchor(event.currentTarget);
    setMenuTarget(cat);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await feeCategoryService.deleteFeeCategory(deleteTarget._id);
      enqueueSnackbar(`Deleted “${deleteTarget.name}”`, { variant: 'success' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      load();
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to delete category', { variant: 'error' });
    }
  };

  const empty = !loading && categories.length === 0;

  const skeletonRows = useMemo(() =>
    Array.from({ length: 3 }).map((_, i) => (
      <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" width="60%" />
          </Box>
        </Stack>
      </Paper>
    )),
    []);

  return (
    <Box>
      {/* Search & Add — the page around this panel carries its title. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name or code…"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
            ),
          }}
        />
        {hasWrite && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
          >
            Add Category
          </Button>
        )}
      </Stack>

      {/* List */}
      {loading ? (
        <Box>{skeletonRows}</Box>
      ) : empty ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Sell sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
            {debouncedSearch ? 'No matching fee categories' : 'No fee categories yet'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {debouncedSearch
              ? 'Try a different search or clear the filter.'
              : 'Add your first fee head — Tuition, Transport, Exam Fee, whatever your school charges.'}
          </Typography>
          {hasWrite && !debouncedSearch && (
            <Button variant="outlined" startIcon={<Add />} onClick={openCreate}>Add Category</Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={1}>
          {categories.map((cat) => {
            const taxPct = cat.taxRate != null ? Math.round(cat.taxRate * 10000) / 100 : null;
            return (
              <Paper
                key={cat._id}
                variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  borderColor: alpha(accent, 0.16),
                  '&:hover': { borderColor: alpha(accent, 0.32), bgcolor: alpha(accent, 0.02) },
                  transition: 'border-color 120ms, background-color 120ms',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 32, height: 32, borderRadius: 1.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: alpha(accent, 0.12),
                      color: accent,
                      flexShrink: 0,
                    }}
                  >
                    <Sell fontSize="small" />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        {cat.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                          px: 0.75, py: 0.15,
                          borderRadius: 0.75,
                        }}
                      >
                        {cat.code}
                      </Typography>
                    </Stack>
                    {cat.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {cat.description}
                      </Typography>
                    )}

                    {/* Badges — kept quiet; the row is scannable at a glance */}
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                      {cat.isOptional && (
                        <Chip
                          size="small"
                          icon={<StarBorder sx={{ fontSize: '0.85rem !important' }} />}
                          label="Optional"
                          variant="outlined"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 500,
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            color: theme.palette.warning.dark,
                            bgcolor: alpha(theme.palette.warning.main, 0.06),
                            '& .MuiChip-icon': { color: 'inherit', ml: '4px', mr: '-2px' },
                          }}
                        />
                      )}
                      {!cat.isRefundable && (
                        <Chip
                          size="small"
                          label="Non-refundable"
                          variant="outlined"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 500,
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            color: theme.palette.error.dark,
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                          }}
                        />
                      )}
                      {taxPct != null && (
                        <Chip
                          size="small"
                          label={`Tax ${taxPct}%`}
                          variant="outlined"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 500,
                            borderColor: alpha(theme.palette.text.primary, 0.16),
                            color: 'text.secondary',
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {hasWrite && (
                    <IconButton size="small" onClick={(e) => openMenu(e, cat)} aria-label="Options">
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Row menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => menuTarget && openEdit(menuTarget)}>
          <Edit fontSize="small" sx={{ mr: 1.25 }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuTarget && openDelete(menuTarget)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.25 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / edit dialog */}
      <FeeCategoryDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />

      {/* Confirm delete */}
      <ConfirmationDialog
        open={deleteOpen}
        title="Delete fee category?"
        message={
          deleteTarget
            ? `“${deleteTarget.name}” will be hidden from new plans and challans. Existing plans that reference it will still show the historical name.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        onConfirm={confirmDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default FeeCategoriesSettings;
